import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Switch, TextField, Typography,
} from '@mui/material';
import api from '../config/api';
import LocationAutocomplete from '../components/LocationAutocomplete';
import MemoriesInviteEmails from '../components/MemoriesInviteEmails';
import { SingleImageUploader } from '../components/ImageUploader';
import { DOCUMENT, PAGE_CONTAINER_MAX_WIDTH, documentPageContainerSx } from '../constants/documentTheme';
import { partnerOutlineBtnSx } from '../components/PartnerListChrome';
import { formatMemoryDate, memoriesPath } from '../utils/memoriesHost';

async function uploadSlideshowMusic(slug, file) {
  const body = new FormData();
  body.append('file', file);
  const { data } = await api.post(`/memories/events/${slug}/slideshow-music`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.event;
}

async function clearSlideshowMusic(slug) {
  const { data } = await api.delete(`/memories/events/${slug}/slideshow-music`);
  return data.event;
}

const emptyPlace = { name: '', placeId: '', lat: null, lng: null };

const MemoriesCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug: editSlug } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(editSlug);
  const presetTripId = searchParams.get('tripId');
  const [source, setSource] = useState(presetTripId ? 'trip' : 'new');
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState(presetTripId || '');
  const [linkedTripTitle, setLinkedTripTitle] = useState('');
  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [place, setPlace] = useState(emptyPlace);
  const [theme, setTheme] = useState('parchment');
  const [notes, setNotes] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [allowGuestUploads, setAllowGuestUploads] = useState(false);
  const [slideshowMusicName, setSlideshowMusicName] = useState('');
  const [slideshowMusicFile, setSlideshowMusicFile] = useState(null);
  const [removeSlideshowMusic, setRemoveSlideshowMusic] = useState(false);
  const musicInputRef = useRef(null);
  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteDraft, setInviteDraft] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) return undefined;
    api.get('/memories/trips/linkable').then(({ data }) => {
      setTrips(data.trips || []);
    }).catch(() => {});
    return undefined;
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/memories/events/${editSlug}`);
        if (cancelled) return;
        const event = data.event;
        if (!event?.is_owner) {
          setError(t('memories.editForbidden'));
          return;
        }
        setName(event.name || '');
        setStartsOn(event.starts_on || '');
        setEndsOn(event.ends_on || '');
        setPlace({
          name: event.place_name || event.place_formatted || '',
          placeId: event.place_id || '',
          lat: event.lat ?? null,
          lng: event.lng ?? null,
        });
        setTheme(event.theme || 'parchment');
        setNotes(event.notes || '');
        setCoverImageUrl(event.cover_image_url || event.cover_url || '');
        setAllowGuestUploads(Boolean(event.allow_guest_uploads));
        setSlideshowMusicName(event.slideshow_music_filename || '');
        setSlideshowMusicFile(null);
        setRemoveSlideshowMusic(false);
        setSource(event.trip_id ? 'trip' : 'new');
        setTripId(event.trip_id || '');
        setLinkedTripTitle(event.trip_title || '');
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('memories.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editSlug, isEdit, t]);

  const chooseSource = (value) => {
    if (isEdit) return;
    setSource(value);
    if (value === 'new') {
      setName('');
      setStartsOn('');
      setEndsOn('');
      setPlace(emptyPlace);
    }
  };

  const buildPayload = () => ({
    name,
    startsOn: startsOn || null,
    endsOn: endsOn || null,
    placeId: place.placeId || null,
    placeName: place.name || null,
    placeFormatted: place.name || null,
    lat: place.lat,
    lng: place.lng,
    theme,
    notes: notes || null,
    allowGuestUploads,
    coverImageUrl: coverImageUrl || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const { data } = await api.patch(`/memories/events/${editSlug}`, buildPayload());
        const slug = data.event.slug || editSlug;
        if (removeSlideshowMusic && !slideshowMusicFile) {
          await clearSlideshowMusic(slug);
        } else if (slideshowMusicFile) {
          await uploadSlideshowMusic(slug, slideshowMusicFile);
        }
        if (inviteEmails.length > 0) {
          await api.post(`/memories/events/${slug}/invites/email`, { emails: inviteEmails });
        }
        navigate(memoriesPath(`e/${slug}`));
        return;
      }
      const { data } = await api.post('/memories/events', {
        tripId: source === 'trip' ? tripId || undefined : undefined,
        name,
        startsOn: startsOn || undefined,
        endsOn: endsOn || undefined,
        placeId: place.placeId || undefined,
        placeName: place.name || undefined,
        placeFormatted: place.name || undefined,
        lat: place.lat,
        lng: place.lng,
        theme,
        notes: notes || undefined,
        allowGuestUploads,
        inviteEmails: source === 'new' ? inviteEmails : undefined,
        coverImageUrl: source === 'new' && coverImageUrl ? coverImageUrl : undefined,
      });
      const slug = data.event.slug;
      if (slideshowMusicFile && slug) {
        await uploadSlideshowMusic(slug, slideshowMusicFile);
      }
      navigate(memoriesPath(`e/${slug}`));
    } catch (err) {
      setError(err.response?.data?.error || (isEdit ? t('memories.updateFailed') : t('memories.createFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    setError('');
    try {
      await api.patch(`/memories/events/${editSlug}`, { status: 'archived' });
      setArchiveOpen(false);
      navigate(memoriesPath());
    } catch (err) {
      setError(err.response?.data?.error || t('memories.archiveFailed'));
    } finally {
      setArchiving(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '2px',
      backgroundColor: '#fff',
    },
  };

  const showDetails = isEdit || source === 'new';
  const submitDisabled = saving
    || loading
    || (!isEdit && source === 'trip' && !tripId)
    || (showDetails && !String(name || '').trim());

  if (loading) {
    return (
      <Box sx={{ backgroundColor: DOCUMENT.canvas, minHeight: '100vh', pb: 8 }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, pt: 6 }}>
          <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: DOCUMENT.canvas, minHeight: '100vh', pb: 8 }}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, pt: 6 }}>
        <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.accent }}>
          {isEdit ? t('memories.editSpace') : t('memories.newSpace')}
        </Typography>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 36, color: DOCUMENT.ink, mb: 3 }}>
          {isEdit ? t('memories.editRemembering') : t('memories.whatRemembering')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ backgroundColor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.rule}`, p: 3 }}>
          {!isEdit && (
            <>
              <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
                {t('memories.source')}
              </Typography>
              <Box sx={{ display: 'flex', border: `1px solid ${DOCUMENT.rule}`, width: 'fit-content', mb: 3 }}>
                {['trip', 'new'].map((value) => (
                  <Button
                    key={value}
                    type="button"
                    onClick={() => chooseSource(value)}
                    sx={{
                      borderRadius: 0,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      fontSize: 11,
                      px: 2,
                      color: source === value ? DOCUMENT.ivory : DOCUMENT.ink3,
                      backgroundColor: source === value ? DOCUMENT.oxblood : '#fff',
                    }}
                  >
                    {value === 'trip' ? t('memories.linkATrip') : t('memories.startFresh')}
                  </Button>
                ))}
              </Box>
            </>
          )}

          {isEdit && source === 'trip' && (
            <Box sx={{ border: `1px solid ${DOCUMENT.rule}`, mb: 2, backgroundColor: '#fff', p: 1.5 }}>
              <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 0.5 }}>
                {t('memories.linkedTrip')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink }}>
                {linkedTripTitle || t('memories.tripBadge')}
              </Typography>
            </Box>
          )}

          {!isEdit && source === 'trip' && (
            <Box sx={{ border: `1px solid ${DOCUMENT.rule}`, mb: 2, backgroundColor: '#fff' }}>
              {trips.length === 0 && (
                <Typography sx={{ p: 2, color: DOCUMENT.ink3 }}>{t('memories.noLinkableTrips')}</Typography>
              )}
              {trips.map((trip) => (
                <Box
                  key={trip.id}
                  component="label"
                  sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, borderBottom: `1px solid ${DOCUMENT.rule}` }}
                >
                  <input
                    type="radio"
                    name="trip"
                    checked={String(tripId) === String(trip.id)}
                    onChange={() => setTripId(trip.id)}
                  />
                  <Box>
                    <Typography sx={{ color: DOCUMENT.ink }}>{trip.title}</Typography>
                    <Typography sx={{ letterSpacing: '0.06em', fontSize: 10, color: DOCUMENT.ink3 }}>
                      {formatMemoryDate(trip.start_date) || trip.start_date}
                      {' · '}
                      <Box component="span" sx={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {trip.member_count} {t('memories.travellers')}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {showDetails && (
            <>
              <TextField fullWidth required label={t('memories.name')} value={name} onChange={(e) => setName(e.target.value)} sx={{ ...fieldSx, mb: 2 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                <TextField type="date" label={t('memories.starts')} InputLabelProps={{ shrink: true }} value={startsOn} onChange={(e) => setStartsOn(e.target.value)} sx={fieldSx} />
                <TextField type="date" label={t('memories.ends')} InputLabelProps={{ shrink: true }} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} sx={fieldSx} />
              </Box>
              <LocationAutocomplete
                value={place.name}
                onChange={(value) => setPlace((prev) => ({ ...prev, name: value }))}
                onPlaceSelect={(placeData) => setPlace({
                  name: placeData.address,
                  placeId: placeData.placeId || '',
                  lat: placeData.coordinates?.lat || null,
                  lng: placeData.coordinates?.lng || null,
                })}
                label={t('memories.location')}
                sx={{ mb: 2 }}
              />
              <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
                {t('memories.theme')}
              </Typography>
              <Box sx={{ display: 'flex', border: `1px solid ${DOCUMENT.rule}`, width: 'fit-content', mb: 2 }}>
                {['parchment', 'evening', 'partner'].map((value) => (
                  <Button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    sx={{
                      borderRadius: 0,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      fontSize: 11,
                      color: theme === value ? DOCUMENT.ivory : DOCUMENT.ink3,
                      backgroundColor: theme === value ? DOCUMENT.oxblood : '#fff',
                    }}
                  >
                    {t(`memories.theme_${value}`)}
                  </Button>
                ))}
              </Box>
              <TextField fullWidth label={t('memories.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ ...fieldSx, mb: 2 }} />
              <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
                {t('memories.coverImage')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 1.5 }}>
                {t('memories.coverHint')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <SingleImageUploader
                  value={coverImageUrl}
                  onChange={(url) => setCoverImageUrl(url || '')}
                  preset="cover"
                  folder="memories/covers"
                  label={t('memories.coverBrowse')}
                  cropAspect={16 / 9}
                />
              </Box>

              <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
                {t('memories.slideshowMusic')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 1.5 }}>
                {t('memories.slideshowMusicHint')}
              </Typography>
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  border: `1px solid ${DOCUMENT.rule}`,
                  backgroundColor: DOCUMENT.canvas,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1.25,
                }}
                data-testid="memories-slideshow-music"
              >
                <input
                  ref={musicInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/ogg,audio/webm,.mp3,.wav,.m4a,.aac,.ogg,.webm"
                  hidden
                  data-testid="memories-slideshow-music-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setSlideshowMusicFile(file);
                    setRemoveSlideshowMusic(false);
                    if (file) setSlideshowMusicName(file.name);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  onClick={() => musicInputRef.current?.click()}
                  sx={partnerOutlineBtnSx}
                >
                  {t('memories.slideshowMusicBrowse')}
                </Button>
                <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13, flex: '1 1 160px' }}>
                  {slideshowMusicFile || (slideshowMusicName && !removeSlideshowMusic)
                    ? (slideshowMusicFile?.name || slideshowMusicName)
                    : t('memories.slideshowMusicDefault')}
                </Typography>
                {(slideshowMusicFile || (slideshowMusicName && !removeSlideshowMusic)) && (
                  <Button
                    type="button"
                    onClick={() => {
                      setSlideshowMusicFile(null);
                      setSlideshowMusicName('');
                      setRemoveSlideshowMusic(Boolean(isEdit));
                    }}
                    data-testid="memories-slideshow-music-clear"
                    sx={{ color: DOCUMENT.ink3, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 11 }}
                  >
                    {t('memories.slideshowMusicClear')}
                  </Button>
                )}
              </Box>

              <MemoriesInviteEmails
                emails={inviteEmails}
                onChange={setInviteEmails}
                draft={inviteDraft}
                onDraftChange={setInviteDraft}
                fieldSx={fieldSx}
              />
              <FormControlLabel
                control={<Switch checked={allowGuestUploads} onChange={(e) => setAllowGuestUploads(e.target.checked)} />}
                label={t('memories.allowGuests')}
                sx={{ mb: 2, color: DOCUMENT.ink2 }}
              />
            </>
          )}
          {error && <Typography sx={{ color: '#9C3B2A', mb: 2 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="submit"
              disabled={submitDisabled}
              sx={{
                backgroundColor: DOCUMENT.oxblood,
                color: DOCUMENT.ivory,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                borderRadius: '2px',
                '&:hover': { backgroundColor: '#5C1A22', color: DOCUMENT.ivory },
                '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.45 },
              }}
            >
              {isEdit
                ? t('memories.saveChanges')
                : (source === 'trip' ? t('memories.createFromTrip') : t('memories.createSpace'))}
            </Button>
            <Button
              type="button"
              onClick={() => navigate(isEdit ? memoriesPath(`e/${editSlug}`) : memoriesPath())}
              sx={{ color: DOCUMENT.ink2 }}
            >
              {t('common.cancel')}
            </Button>
            {isEdit && (
              <Button
                type="button"
                onClick={() => setArchiveOpen(true)}
                sx={{ color: '#9C3B2A', ml: { sm: 'auto' }, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 12 }}
              >
                {t('memories.archiveSpace')}
              </Button>
            )}
          </Box>
        </Box>
      </Container>

      <Dialog open={archiveOpen} onClose={() => !archiving && setArchiveOpen(false)}>
        <DialogTitle>{t('memories.archiveSpaceTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('memories.archiveSpaceBody', { name: name || '' })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveOpen(false)} disabled={archiving} sx={{ color: DOCUMENT.ink2 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleArchive}
            disabled={archiving}
            sx={{ color: '#9C3B2A', letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            {archiving ? t('common.loading') : t('memories.archiveSpaceConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoriesCreate;
